import { GearIcon, PlusIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'

import { useGetGroups } from '@/api/groups/get-groups'
import {
  GET_PENDING_COMMENTS_QUERY_KEY,
  useGetPendingComments,
} from '@/api/groups/get-pending-comments'
import { GET_POSTS_BASE_QUERY_KEY, useGetPosts } from '@/api/groups/get-posts'
import Dashboard from '@/components/Dashboard'
import PostForm from '@/components/Groups/GroupPostsDashboard/PostForm/PostForm'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { routerFactory } from '@/factories'
import { selectActiveBuilding, selectBuildings } from '@/reducers/buildingSelectorReducer'
import { createPosts } from '@/reducers/Organisation/groupsReducer'
import { selectUserRole } from '@/reducers/userDataReducer'
import { useAppDispatch } from '@/store/hooks'
import { resetQueries } from '@/utils'

export const GroupsPage = () => {
  const { t } = useTranslation(['groups'])
  const location = useLocation()
  const { navigate } = routerFactory.useRouter()
  const dispatch = useAppDispatch()

  const activeBuilding = useSelector(selectActiveBuilding)
  const userRole = useSelector(selectUserRole)
  const buildings = useSelector(selectBuildings)

  const { data: groups, isLoading } = useGetGroups({
    buildingId: activeBuilding?.id,
  })
  const commentsApprovalEnabledInAnyGroups = groups?.some((group) => group.approveComments)

  const [newPostFormVisible, setNewPostFormVisible] = useState(false)

  const createGroupPosts = useCallback(
    async (groupIds: number[], newPostData: Record<string, unknown>) => {
      dispatch(createPosts(groupIds, newPostData))
    },
    [dispatch],
  )
  const { data: pendingCommentsData } = useGetPendingComments({
    buildingId: activeBuilding?.id,
    limit: DEFAULT_LIMIT,
    skip: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  })
  const { data: posts } = useGetPosts({
    buildingId: activeBuilding?.id,
    limit: DEFAULT_LIMIT,
    skip: 0,
  })

  useEffect(() => {
    const isPostTabOpen = location.pathname.includes('/posts')
    if (!isLoading && !commentsApprovalEnabledInAnyGroups && !isPostTabOpen) {
      navigate({ path: '/dashboard/building-dashboard/groups/posts' })
    }
  }, [commentsApprovalEnabledInAnyGroups, isLoading, location.pathname, navigate])

  useEffect(() => {
    if (location.pathname.endsWith('/posts') || location.pathname.endsWith('/comments')) {
      resetQueries([GET_POSTS_BASE_QUERY_KEY], [GET_PENDING_COMMENTS_QUERY_KEY])
    }
  }, [location.pathname])

  // TODO: Remove Dashboard.Container from here. Padding and background color has to be provided to match rest of the pages.
  return (
    <Dashboard.Container className={`ctp-groups-page__wrapper`}>
      <Page>
        <PageHeader
          title={t('groups:list.title')}
          actions={
            <>
              <Button
                use="primary"
                size="small"
                onClick={() => navigate({ path: '/dashboard/building-dashboard/groups-settings' })}
              >
                <Icon icon={GearIcon} />
                {t('groups:list.settingsButton')}
              </Button>
              <Button use="secondary" size="small" onClick={() => setNewPostFormVisible(true)}>
                {t('groups:list.newPostButton')}
                <Icon icon={PlusIcon} />
              </Button>
            </>
          }
          navbarItems={[
            {
              count: posts?.unapprovedCount,
              hidden: !commentsApprovalEnabledInAnyGroups,
              label: t('groups:list.navbar.posts'),
              to: { path: '/dashboard/building-dashboard/groups/posts' },
            },
            {
              count: pendingCommentsData?.count,
              hidden: !commentsApprovalEnabledInAnyGroups,
              isDanger: true,
              label: t('groups:list.navbar.comments'),
              to: { path: '/dashboard/building-dashboard/groups/comments' },
            },
          ]}
        />
        <Outlet />
      </Page>
      {newPostFormVisible && (
        <PostForm
          innerGroups
          createPost={createGroupPosts}
          updatePost={() => {}}
          editPostData={null}
          handleCancel={() => setNewPostFormVisible(false)}
          userRole={userRole}
          buildings={buildings}
          buildingId={null}
          buildingTimezone="America/Chicago"
          onSuccess={() => {}}
        />
      )}
    </Dashboard.Container>
  )
}
